import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase, readableError } from '../../lib/supabase'
import { useSession } from '../../lib/session'
import { usePlatform } from '../../lib/platform'
import { toTitleCase } from '../../lib/format'
import { toChordPro } from '../../lib/chords'
import { Shell } from '../../components/Shell'
import PortalFrame from '../../components/PortalFrame'
import { Player, PcoIcon, Sill, Spinner, TrustRow, Check, DoneIcon, Field, sectionLabel, initials } from '../../components/ui'
import Passage from '../../components/Passage'
import { translationLabel } from '../../lib/bible'

const DECISIONS = [
  ['closer_look', 'A closer look', 'The first yes — move it toward Sunday testing'],
  ['paired', 'Not yet — pair them up', 'Send it back with a co-writer to develop'],
  ['rework', 'Send back for a rework', 'Close, needs another pass'],
]

const CANNED = {
  closer_look: 'This is the one. I want to give it a closer look and aim it at a Sunday.',
  paired: "There's something here. I'd love to put you with another writer to develop it.",
  rework: "Really close. Give it another pass and send it back to me — I'd like to see where it lands.",
}

const TIER_LABELS = ['Growing', 'Sound', 'Strong']
const DEFAULT_PRIV = { overall: 1, lyrics: 1, theology: 2, fit: 0, text: '' }

/**
 * What a reviewer thought, kept for themselves. Never shown to the writer,
 * another reviewer, or an admin at the same church — song_reviewer_notes'
 * RLS scopes every row to reviewer_id = auth.uid(), so this is the only
 * component that will ever read one back.
 */
function PrivateNotes({ priv, onTier, onText, saved }) {
  return (
    <div className="notesbox">
      <p className="notestitle">Your notes</p>
      <span className="privatepill"><span className="dot" />Private — only you see this</span>
      <p className="notesub">
        A place to remember what you thought, in case someone asks about this song later.
        It's not shown to the writer, and it's optional.
      </p>

      {[
        ['overall', 'Overall'], ['lyrics', 'Lyrics'], ['theology', 'Theology'], ['fit', 'Fit for us'],
      ].map(([key, label]) => (
        <div className="slider-row" key={key}>
          <div className="slider-top">
            <span className="lbl">{label}</span>
            <span className="val">{TIER_LABELS[priv[key]]}</span>
          </div>
          <input type="range" min="0" max="2" value={priv[key]}
                 onChange={(e) => onTier(key, Number(e.target.value))} />
        </div>
      ))}

      <textarea className="ta" value={priv.text} placeholder="Note to yourself…"
                onChange={(e) => onText(e.target.value, false)}
                onBlur={(e) => onText(e.target.value, true)} />
      {!saved && <p className="small" style={{ marginTop: 6 }}>Saving…</p>}
    </div>
  )
}

/** "Sun 12 Oct, 4:30pm" — short enough to sit in a decision note without dominating it. */
const formatMeetingTime = (iso) => new Date(iso).toLocaleString(undefined, {
  weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
})

export default function Review() {
  const { id } = useParams()
  const nav = useNavigate()
  const { user, active } = useSession()
  const { device } = usePlatform()
  const isPhone = device === 'phone'

  const [song, setSong] = useState(null)
  const [ready, setReady] = useState(false)
  const [cowriters, setCowriters] = useState([])
  const [sections, setSections] = useState([])
  const [gate, setGate] = useState(null)
  const [src, setSrc] = useState(null)
  const [showWords, setShowWords] = useState(false)
  const [choice, setChoice] = useState(null)
  const [note, setNote] = useState('')
  const [showMeeting, setShowMeeting] = useState(false)
  const [meetingTimes, setMeetingTimes] = useState({ a: '', b: '' })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [done, setDone] = useState(false)
  const [priv, setPriv] = useState(DEFAULT_PRIV)
  const [privSaved, setPrivSaved] = useState(true)
  const [pushing, setPushing] = useState(false)
  const [pushed, setPushed] = useState(null) // { partial } once it lands
  const [pushErr, setPushErr] = useState(null)

  const load = useCallback(async () => {
    const [{ data: s, error }, { data: cw }, { data: sec }, { data: g }, { data: pn }] = await Promise.all([
      supabase.from('songs').select('*').eq('id', id).maybeSingle(),
      supabase.from('song_cowriters').select('*').eq('song_id', id).order('is_primary', { ascending: false }),
      supabase.from('song_sections').select('*').eq('song_id', id).order('order_position'),
      supabase.from('song_cosign_status').select('*').eq('song_id', id).maybeSingle(),
      supabase.from('song_reviewer_notes').select('*').eq('song_id', id).eq('reviewer_id', user.id).maybeSingle(),
    ])
    if (error) setErr(readableError(error))
    if (pn) {
      setPriv({
        overall: pn.rating_overall ?? DEFAULT_PRIV.overall,
        lyrics: pn.rating_lyrics ?? DEFAULT_PRIV.lyrics,
        theology: pn.rating_theology ?? DEFAULT_PRIV.theology,
        fit: pn.rating_fit ?? DEFAULT_PRIV.fit,
        text: pn.note || '',
      })
    }
    setSong(s ?? null); setCowriters(cw ?? []); setSections(sec ?? []); setGate(g ?? null)
    setReady(true)
  }, [id, user.id])

  useEffect(() => { load() }, [load])

  const persistPriv = async (next) => {
    setPrivSaved(false)
    const { error } = await supabase.from('song_reviewer_notes').upsert({
      song_id: id, reviewer_id: user.id,
      rating_overall: next.overall, rating_lyrics: next.lyrics,
      rating_theology: next.theology, rating_fit: next.fit,
      note: next.text.trim() || null,
    }, { onConflict: 'song_id,reviewer_id' })
    setPrivSaved(!error)
  }

  const privTier = (key, value) => {
    const next = { ...priv, [key]: value }
    setPriv(next)
    persistPriv(next)
  }

  /**
   * Sends the song into the church's Planning Center library.
   *
   * Available during review, not only after a decision: the rehearsal licence
   * is granted the moment a writer submits, precisely so the band can play it
   * before anyone decides anything. "Band run through" is a step in the
   * church's own process for the same reason.
   *
   * The chart is rendered here because only the browser has the music theory
   * to turn stored scale degrees into letters — see toChordPro.
   */
  const pushToPco = async () => {
    setPushing(true); setPushErr(null)
    const { data, error } = await supabase.functions.invoke('pco-push-song', {
      body: {
        song_id: song.id,
        chord_chart: sections.length
          ? toChordPro(sections, { key: song.key || 'C' })
          : null,
      },
    })
    setPushing(false)
    if (error) return setPushErr(readableError(error))
    setPushed(data ?? { partial: false })
  }

  const privText = (text, blurred) => {
    const next = { ...priv, text }
    setPriv(next)
    if (blurred) persistPriv(next)
  }

  useEffect(() => {
    if (!song?.r2_object_key) return
    supabase.functions.invoke('r2-playback-url', { body: { song_id: song.id } })
      .then(({ data }) => setSrc(data?.url ?? null))
  }, [song?.id, song?.r2_object_key])

  const Frame = isPhone
    ? ({ children, ...p }) => <Shell {...p}>{children}</Shell>
    : ({ children, title, sub, back, backLabel }) =>
        <PortalFrame title={title} sub={sub} back={back} backLabel={backLabel}>{children}</PortalFrame>

  if (!ready) return <Frame><Spinner /></Frame>
  if (!song) return (
    <Frame back="/inbox" title={isPhone ? 'Needs you' : "That song isn't in your inbox."}
           backLabel="Inbox">
      {isPhone && <h2 className="q">That song isn't in your inbox.</h2>}
      <p className="sub">It may have been decided already, or it belongs to another church.</p>
    </Frame>
  )

  /** Folds the proposed times into the note, since decision_note is the only
   *  place a meeting offer can travel — there's no separate column for it. */
  const addMeetingTimes = () => {
    const times = [meetingTimes.a, meetingTimes.b].filter(Boolean).map(formatMeetingTime)
    if (!times.length) return
    const text = `Proposed times: ${times.join(' or ')}`
    setNote((n) => (n ? `${n}\n\n${text}` : text))
    setShowMeeting(false)
    setMeetingTimes({ a: '', b: '' })
  }

  const decide = async () => {
    setBusy(true); setErr(null)
    // decision_by / decision_at are stamped by the guard trigger, not trusted from here.
    const { error } = await supabase.from('songs').update({
      decision: choice,
      status: choice,
      decision_note: note.trim() || null,
    }).eq('id', song.id)
    setBusy(false)
    if (error) return setErr(readableError(error))
    setDone(true)
  }

  if (done) {
    const label = DECISIONS.find((d) => d[0] === choice)
    return (
      <Frame>
        <div className={isPhone ? undefined : 'pf-card'} style={isPhone ? undefined : { maxWidth: 520, margin: '0 auto', textAlign: 'center' }}>
          <DoneIcon />
          <h2 className="q center">{label[1]}</h2>
          <p className="sub center">
            {toTitleCase(song.title)} moves on. The writers hear it from you, warmly — never a cold notification.
          </p>
          <div className="trust"><TrustRow title={label[2]} /></div>
          <div className="hr" />
          <button className="btn btn-quiet" onClick={() => nav('/inbox')}>
            {isPhone ? 'Back to Needs you' : 'Back to the inbox'}
          </button>
        </div>
      </Frame>
    )
  }

  const names = cowriters.map((c) => c.cowriter_name).join(' & ')
  const splits = cowriters.map((c) => c.split_percentage).join(' / ')
  const scripture = (song.scripture_refs || []).join(', ')

  /* ---------- desktop / tablet ---------- */
  if (!isPhone) {
    const canRespond = active?.can_leave_freetext || active?.can_use_canned_responses
    return (
      <PortalFrame back="/inbox" backLabel="Back to inbox">
        <div className="pf-cols pf-cols-2">
          <div>
            <div className="pf-card" style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span className="pf-pill pf-pill-review">In review</span>
                {song.song_number && <span className="pf-song-n">#{song.song_number}</span>}
              </div>
              <h1 className="pf-h1" style={{ fontSize: 27 }}>{toTitleCase(song.title)}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 11 }}>
                <span className="pf-song-ava">{initials(names)}</span>
                <span style={{ font: '600 13px Inter', color: 'var(--text)' }}>{names}</span>
                {scripture && <span className="pf-pill pf-pill-ready">{scripture}</span>}
                {song.key && <span className="pf-pill pf-pill-ready">in {song.key}</span>}
              </div>
              {song.season_note && (
                <p style={{ margin: '13px 0 0', padding: '11px 13px', borderRadius: 12,
                            background: 'var(--raised)', fontSize: 13, color: 'var(--dim)',
                            fontStyle: 'italic' }}>
                  “{song.season_note}”
                </p>
              )}
            </div>

            <div className="pf-card" style={{ marginBottom: 14 }}>
              <div className="pf-cardhead"><span className="pf-cardtitle">Recording</span></div>
              <Player src={src} mime={song.media_mime} duration={song.duration_seconds}
                      filename={song.media_filename} />
              {!song.r2_object_key && <p className="pf-empty">No recording attached.</p>}
            </div>

            {song.scripture_text && (
              <div className="pf-card" style={{ marginBottom: 14 }}>
                <div className="pf-cardhead"><span className="pf-cardtitle">Scripture</span></div>
                <Passage verses={song.scripture_verses} text={song.scripture_text}
                         reference={scripture} translation={song.scripture_translation} />
              </div>
            )}

            <div className="pf-card">
              <div className="pf-cardhead">
                <span className="pf-cardtitle">Chart &amp; lyric sheet</span>
                {sections.length > 0 && (
                  <button className="pf-btn pf-btn-quiet" onClick={() => setShowWords(!showWords)}>
                    {showWords ? 'Hide the words' : 'Read the words'}
                  </button>
                )}
              </div>
              {sections.length === 0 && <p className="pf-empty">No lyric sheet attached.</p>}
              {showWords && sections.map((s) => (
                <div key={s.id} style={{ marginBottom: 14 }}>
                  <div className="label">{sectionLabel(s)}</div>
                  <p style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 13.5 }}>{s.lyrics}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="pf-card" style={{ marginBottom: 14 }}>
              <div className="pf-cardhead"><span className="pf-cardtitle">Co-writers &amp; splits</span></div>
              <p className="pf-empty" style={{ marginBottom: 6 }}>
                {gate?.all_signed
                  ? `Every writer below has signed — splits total ${gate?.total_split ?? 100}%.`
                  : 'Not every writer has signed yet.'}
              </p>
              {cowriters.map((c) => (
                <div key={c.id} className="pf-cw">
                  <span className="pf-song-ava">{initials(c.cowriter_name)}</span>
                  <span className="pf-cw-n">{c.cowriter_name}</span>
                  <span className="pf-cw-s">{c.split_percentage}%</span>
                  {c.signed && <Check />}
                </div>
              ))}
            </div>

            <div className="pf-card" style={{ marginBottom: 14 }}>
              <div className="pf-cardhead">
                <span className="pf-cardtitle">Planning Center</span>
                <PcoIcon size={16} />
              </div>
              {pushed ? (
                <p className="pf-empty">
                  <span className="pf-pill pf-pill-ok">In your library</span>{' '}
                  {pushed.already
                    ? 'This song was already pushed — it has not been added twice.'
                    : pushed.partial
                      ? 'The song is there, but the chord chart did not attach. Worth adding by hand.'
                      : 'Song, writers and chord chart are all in Services.'}
                </p>
              ) : (
                <>
                  <p className="pf-empty" style={{ marginBottom: 8 }}>
                    Put this in your Services library so the band can play it. Rehearsal
                    permission is already granted — the writer gave it when they sent the song.
                  </p>
                  <button className="pf-btn pf-btn-solid" disabled={pushing} onClick={pushToPco}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <PcoIcon size={14} />
                    {pushing ? 'Sending…' : 'Push to Planning Center'}
                  </button>
                </>
              )}
              {pushErr && <p className="err">{pushErr}</p>}
            </div>

            <PrivateNotes priv={priv} onTier={privTier} onText={privText} saved={privSaved} />

            <div className="pf-card">
              <div className="pf-cardhead"><span className="pf-cardtitle">Your response</span></div>
              {DECISIONS.map(([k, title, hint]) => (
                <button key={k} className={`pf-decide${choice === k ? ' on' : ''}`}
                        onClick={() => { setChoice(k); if (active?.can_use_canned_responses && !note) setNote(CANNED[k]) }}>
                  {title}<small>{hint}</small>
                </button>
              ))}

              {choice && canRespond && (
                <div style={{ marginTop: 10 }}>
                  <textarea className="ta" value={note} readOnly={!active?.can_leave_freetext}
                            placeholder="Add a warm note (optional)"
                            onChange={(e) => setNote(e.target.value)} />
                  <p className="pf-empty">
                    {active?.can_leave_freetext
                      ? 'Available because you can leave free-text notes.'
                      : 'Your church has free-text notes turned off for your seat.'}
                  </p>
                  {active?.can_use_canned_responses && (
                    <button className="pf-btn" onClick={() => setNote(CANNED[choice])}>
                      Use the warm reply
                    </button>
                  )}
                </div>
              )}

              {choice && (
                <button className="btn btn-primary" style={{ marginTop: 12 }} disabled={busy} onClick={decide}>
                  {busy ? 'Sending…' : 'Send my decision'}
                </button>
              )}

              {active?.can_set_meetings && (
                <>
                  <button className="pf-btn" style={{ width: '100%', marginTop: 8 }}
                          onClick={() => setShowMeeting(!showMeeting)}>
                    {showMeeting ? 'Hide meeting times' : 'Offer a meeting'}
                  </button>
                  {showMeeting && (
                    <div style={{ marginTop: 10 }}>
                      <Field label="First option">
                        <input type="datetime-local" className="input" value={meetingTimes.a}
                               onChange={(e) => setMeetingTimes({ ...meetingTimes, a: e.target.value })} />
                      </Field>
                      <Field label="Second option (optional)">
                        <input type="datetime-local" className="input" value={meetingTimes.b}
                               onChange={(e) => setMeetingTimes({ ...meetingTimes, b: e.target.value })} />
                      </Field>
                      <button className="pf-btn pf-btn-solid" disabled={!meetingTimes.a}
                              onClick={addMeetingTimes}>
                        Add to note
                      </button>
                    </div>
                  )}
                </>
              )}
              {err && <p className="err">{err}</p>}
            </div>
          </div>
        </div>
      </PortalFrame>
    )
  }

  /* ---------- phone: unchanged ---------- */
  return (
    <Shell back="/inbox" title="Needs you">
      <div className="eyebrow">From {names}</div>
      <h2 className="q">{song.title}</h2>
      <div className="small">{scripture || 'No scripture attached'}{song.key ? ` · in ${song.key}` : ''}</div>
      <Sill />

      <div className="trust">
        <TrustRow ok={song.decl_original} title="Original work"
          hint="Declared by the writers, timestamped" />
        <TrustRow ok={gate?.all_signed} title="Ownership settled"
          hint={`${cowriters.length} writers signed · splits ${splits}`} />
        <TrustRow ok={song.decl_free_to_share} title="Free to share" hint="Not signed elsewhere" />
        <TrustRow ok={!!scripture} title="Scripture attached"
          hint={scripture ? `${scripture}${song.scripture_translation ? ` · ${translationLabel(song.scripture_translation)}` : ''}` : 'None given'} />
      </div>

      {song.scripture_text && (
        <Passage verses={song.scripture_verses} text={song.scripture_text}
                 reference={scripture} translation={song.scripture_translation}
                 style={{ marginBottom: 16 }} />
      )}

      <Player src={src} mime={song.media_mime} duration={song.duration_seconds}
              filename={song.media_filename} />
      {!song.r2_object_key && <p className="small">No recording attached.</p>}

      {sections.length > 0 && (
        <button className="btn-plain" style={{ paddingLeft: 0 }} onClick={() => setShowWords(!showWords)}>
          {showWords ? 'Hide the words ›' : 'Read the words ›'}
        </button>
      )}
      {showWords && sections.map((s) => (
        <div key={s.id} style={{ marginBottom: 14 }}>
          <div className="label">{sectionLabel(s)}</div>
          <p style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 14 }}>{s.lyrics}</p>
        </div>
      ))}

      <div className="hr" />
      <div className="eyebrow">Planning Center</div>
      {pushed ? (
        <p className="small">
          <span className="badge">In your library</span>{' '}
          {pushed.already
            ? 'Already pushed — not added twice.'
            : pushed.partial
              ? 'The song is there, but the chord chart did not attach.'
              : 'Song, writers and chord chart are all in Services.'}
        </p>
      ) : (
        <>
          <p className="small" style={{ marginBottom: 8 }}>
            Put this in your Services library so the band can play it. Rehearsal
            permission came with the song.
          </p>
          <button className="btn btn-quiet" disabled={pushing} onClick={pushToPco}>
            {pushing ? 'Sending…' : 'Push to Planning Center'}
          </button>
        </>
      )}
      {pushErr && <p className="err">{pushErr}</p>}

      <div className="hr" />
      <PrivateNotes priv={priv} onTier={privTier} onText={privText} saved={privSaved} />

      <div className="hr" />
      <div className="eyebrow">Your call</div>
      {DECISIONS.map(([k, title, hint]) => (
        <button key={k} className="action" onClick={() => { setChoice(k); if (active?.can_use_canned_responses && !note) setNote(CANNED[k]) }}
                style={choice === k ? { borderColor: 'var(--accent-deep)' } : undefined}>
          <span>{title}<small>{hint}</small></span>
          <span className="ar">{choice === k ? <Check /> : '›'}</span>
        </button>
      ))}

      {choice && (active?.can_leave_freetext || active?.can_use_canned_responses) && (
        <div style={{ marginTop: 14 }}>
          <label className="label">A word back to the writers</label>
          {active?.can_use_canned_responses && (
            <div className="chips" style={{ marginBottom: 8 }}>
              <button className="chip" onClick={() => setNote(CANNED[choice])}>Use the warm reply</button>
            </div>
          )}
          <textarea className="ta" value={note} readOnly={!active?.can_leave_freetext}
                    onChange={(e) => setNote(e.target.value)} />
          {!active?.can_leave_freetext && (
            <p className="small">Your church has free-text notes turned off for your seat.</p>
          )}
        </div>
      )}

      {choice && (
        <button className="btn btn-primary" style={{ marginTop: 14 }} disabled={busy} onClick={decide}>
          {busy ? 'Sending…' : 'Send my decision'}
        </button>
      )}

      {active?.can_set_meetings && (
        <>
          <button className="btn-plain" style={{ paddingLeft: 0, marginTop: 14 }}
                  onClick={() => setShowMeeting(!showMeeting)}>
            {showMeeting ? 'Hide meeting times' : 'Offer a meeting ›'}
          </button>
          {showMeeting && (
            <div style={{ marginTop: 6 }}>
              <Field label="First option">
                <input type="datetime-local" className="input" value={meetingTimes.a}
                       onChange={(e) => setMeetingTimes({ ...meetingTimes, a: e.target.value })} />
              </Field>
              <Field label="Second option (optional)">
                <input type="datetime-local" className="input" value={meetingTimes.b}
                       onChange={(e) => setMeetingTimes({ ...meetingTimes, b: e.target.value })} />
              </Field>
              <button className="btn btn-quiet" disabled={!meetingTimes.a} onClick={addMeetingTimes}>
                Add to note
              </button>
            </div>
          )}
        </>
      )}

      <p className="small center" style={{ marginTop: 10 }}>
        That's all you have to do. The writer handles the rest.
      </p>
      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
